/*
 * Damder catalog canvas auto-fit.
 * The catalog "pages" place text in absolutely-positioned boxes whose width/height
 * and font-size (cqi units) were calibrated for the original Hebrew wording. When the
 * text is edited or shown in another language, longer words can overflow their box.
 *
 * This shrinks text to fit — but per TIER, not per box: all boxes on a page that share
 * the same calibrated font-size (e.g. every grade name, or every sub-label) shrink
 * together to the size the worst case needs, so a row of labels stays visually uniform.
 * Sizes are in cqi units, so the fit holds proportionally at every viewport width. On
 * narrow screens the overlay switches to a normal stacked layout (CSS media query) and
 * this leaves it untouched.
 */
(function () {
  'use strict';

  function markJustifyBoxes(page) {
    // Reads the actual CSS custom property value via the CSSOM (always
    // normalized to "justify", regardless of how the browser currently
    // happens to serialize the style attribute string) rather than
    // string-matching the attribute, which silently breaks the moment any
    // script sets any other inline style on the element.
    page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text').forEach(function (box) {
      var isJustify = box.style.getPropertyValue('--align').trim() === 'justify';
      box.classList.toggle('damder-catalog-canvas__text--justify-fix', isJustify);
    });
  }

  function primaryBase(box) {
    // Base font-size (cqi) this box was calibrated at — used both to group boxes into
    // tiers and to scale them. Uses the largest span so multi-span boxes group sensibly.
    var max = 0;
    box.querySelectorAll('span').forEach(function (s) {
      if (s.dataset.damderBaseFs === undefined) {
        var m = (s.getAttribute('style') || '').match(/font-size:\s*([\d.]+)cqi/);
        s.dataset.damderBaseFs = m ? m[1] : '';
      }
      if (s.dataset.damderBaseFs) max = Math.max(max, parseFloat(s.dataset.damderBaseFs));
    });
    return max;
  }

  function applyScale(box, scale) {
    box.querySelectorAll('span').forEach(function (s) {
      var base = s.dataset.damderBaseFs;
      if (base) s.style.fontSize = (parseFloat(base) * scale).toFixed(4) + 'cqi';
    });
  }

  function responsiveScale(page) {
    var canvas = page.closest('.damder-catalog-canvas');
    var suffix = window.innerWidth <= 700
      ? 'Mobile'
      : (window.innerWidth <= 1199 ? 'Tablet' : 'Desktop');
    var globalValue = canvas ? parseFloat(canvas.dataset['textScale' + suffix] || '100') : 100;
    var pageValue = parseFloat(page.dataset['pageTextScale' + suffix] || '100');
    return (globalValue / 100) * (pageValue / 100);
  }

  function viewportSuffix() {
    return window.innerWidth <= 700
      ? 'Mobile'
      : (window.innerWidth <= 1199 ? 'Tablet' : 'Desktop');
  }

  var editorFonts = {
    myriad: '"Damder Myriad Hebrew", "Assistant", Arial, sans-serif',
    krakow: '"Damder Krakow", "Frank Ruhl Libre", "Assistant", serif',
    lato: 'Lato, Arial, sans-serif',
    playfair: '"Playfair Display", Georgia, serif',
    oswald: 'Oswald, Arial, sans-serif'
  };

  function rememberInlineProperty(element, property, key) {
    var valueKey = 'damderOriginal' + key;
    var priorityKey = valueKey + 'Priority';
    if (element.dataset[valueKey] === undefined) {
      element.dataset[valueKey] = element.style.getPropertyValue(property) || '';
      element.dataset[priorityKey] = element.style.getPropertyPriority(property) || '';
    }
  }

  function setOrRestoreInlineProperty(element, property, key, value) {
    var valueKey = 'damderOriginal' + key;
    var priorityKey = valueKey + 'Priority';
    rememberInlineProperty(element, property, key);
    if (value) {
      element.style.setProperty(property, value, 'important');
    } else if (element.dataset[valueKey]) {
      element.style.setProperty(property, element.dataset[valueKey], element.dataset[priorityKey] || '');
    } else {
      element.style.removeProperty(property);
    }
  }

  function applyEditorControls(page) {
    var suffix = viewportSuffix();
    var offsetX = parseFloat(page.dataset['textOffsetX' + suffix] || '0');
    var offsetY = parseFloat(page.dataset['textOffsetY' + suffix] || '0');
    var headingFont = editorFonts[page.dataset.headingFontOverride] || '';
    var bodyFont = editorFonts[page.dataset.bodyFontOverride] || '';
    var headingColor = page.dataset.headingColorEnabled === 'true'
      ? page.dataset.headingColorOverride
      : '';
    var bodyColor = page.dataset.bodyColorEnabled === 'true'
      ? page.dataset.bodyColorOverride
      : '';

    page.querySelectorAll('.damder-catalog-canvas__overlay').forEach(function (overlay) {
      // Split mobile artboards render at 2x. Convert editor pixel movement back
      // into their internal coordinate space so 20px means 20 visible pixels.
      var divisor = overlay.closest('.damder-catalog-canvas__mobile-artboard') ? 2 : 1;
      overlay.style.translate = (offsetX / divisor).toFixed(2) + 'px ' + (offsetY / divisor).toFixed(2) + 'px';

      overlay.querySelectorAll('.damder-catalog-canvas__text').forEach(function (box) {
        var isHeading = primaryBase(box) >= 2.8;
        box.querySelectorAll('span').forEach(function (span) {
          setOrRestoreInlineProperty(span, 'font-family', 'FontFamily', isHeading ? headingFont : bodyFont);
          setOrRestoreInlineProperty(span, 'color', 'Color', isHeading ? headingColor : bodyColor);
        });
      });
    });
  }

  function applyBoxTuning(page) {
    // "Manual per-box position — mobile" editor fields: a compact
    // "index:dy:scale:width" list built by the liquid loop, one entry per box
    // that isn't at its default.
    var map = {};
    var raw = page.dataset.boxTuning;
    if (raw) {
      raw.split('|').forEach(function (entry) {
        var parts = entry.split(':');
        var idx = parseInt(parts[0], 10);
        if (idx) {
          map[idx] = {
            dy: parseFloat(parts[1]) || 0,
            scale: (parseFloat(parts[2]) || 100) / 100,
            width: parseFloat(parts[3]) || 0
          };
        }
      });
    }
    var isMobile = viewportSuffix() === 'Mobile';
    var boxes = page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text');
    boxes.forEach(function (box, i) {
      var tuning = map[i + 1];
      box.dataset.damderManualDy = tuning ? tuning.dy : 0;
      box.dataset.damderManualScale = tuning ? tuning.scale : 1;
      // The box's true calibrated width, captured once (before anything ever
      // overrides --w) so every later pass — including rescueOverflow below —
      // has a stable, correct value to fall back to on desktop/tablet.
      if (box.dataset.damderTrueWidth === undefined) {
        var m = (box.getAttribute('style') || '').match(/--w:\s*([\d.]+)%/);
        box.dataset.damderTrueWidth = m ? m[1] : '0';
      }
      var trueW = parseFloat(box.dataset.damderTrueWidth) || 0;
      var manualW = isMobile && tuning && tuning.width ? tuning.width : 0;
      var baseW = manualW || trueW;
      box.style.setProperty('--w', baseW + '%', 'important');
      box.dataset.damderBaseWidth = baseW;
    });
  }

  function rescueOverflow(page) {
    // Last resort for a box that's STILL clipping after fitting/centring/manual
    // tuning: on mobile a description box is often narrow because it was
    // calibrated to sit in a multi-column desktop layout, and its wording was
    // hand-broken into desktop-width lines with <br> tags. Neither constraint
    // matters on a stacked mobile card, so — only for a box that is actually
    // still overflowing — let it wrap naturally instead of on the hard breaks,
    // and reclaim the unused width up to the page's own edge. Same text, same
    // font; every other box is left completely untouched.
    var isMobile = viewportSuffix() === 'Mobile';
    page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text').forEach(function (box) {
      if (box.dataset.damderTrueHtml === undefined) box.dataset.damderTrueHtml = box.innerHTML;
      var originalHtml = box.dataset.damderTrueHtml;
      var baseW = parseFloat(box.dataset.damderBaseWidth || '0');
      // Always start from this pass's base (set by applyBoxTuning above) before
      // judging — safe to call every time regardless of a previous rescue, and
      // required so a box rescued on mobile reverts cleanly back on desktop/tablet.
      if (box.innerHTML !== originalHtml) box.innerHTML = originalHtml;
      box.style.setProperty('--w', baseW + '%', 'important');

      // Short labels (a gem name, a grade code) can trip the overflow check on
      // diacritics alone at the smallest floor size — that's not a wrapping
      // problem <br>-removal or extra width can fix, and on a packed table page
      // (the grade comparison chart) widening a cell risks colliding with its
      // neighbors. Only rescue boxes with real paragraph-length text.
      if (!isMobile || box.textContent.trim().length < 20 || !overflows(box)) return;

      if (originalHtml.indexOf('<br') !== -1) {
        box.innerHTML = originalHtml.replace(/<br\s*\/?>/gi, ' ');
      }
      var style = box.getAttribute('style') || '';
      var xMatch = style.match(/--x:\s*([\d.]+)%/);
      var x = xMatch ? parseFloat(xMatch[1]) : 0;
      // These cards are calibrated as a strict two-column split — a photo/icon on
      // one side, text on the other — and every original box already stops right
      // at that boundary. Growing all the way to the page's own edge (the old
      // rule) ran text straight under the artwork on the other side. Detect which
      // column this box was calibrated for from its TRUE original right edge, and
      // only reclaim width within that same column.
      var trueW = parseFloat(box.dataset.damderTrueWidth) || 0;
      var originalRightEdge = x + trueW;
      var columnCeiling = originalRightEdge <= 55 ? 50 : 100;
      var maxSafeW = Math.max(baseW, columnCeiling - x - 2);
      if (maxSafeW > baseW) box.style.setProperty('--w', maxSafeW + '%', 'important');
    });
  }

  function applyManualBoxScale(page) {
    // Multiplies each box's already-fitted font size by its own manual override.
    // Runs after the shared tier shrink loop so one merchant override never drags
    // the rest of its tier down with it.
    if (viewportSuffix() !== 'Mobile') return;
    page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text').forEach(function (box) {
      var manual = parseFloat(box.dataset.damderManualScale) || 1;
      if (manual === 1) return;
      box.querySelectorAll('span').forEach(function (s) {
        var current = parseFloat(s.style.fontSize);
        if (current) s.style.fontSize = (current * manual).toFixed(4) + 'cqi';
      });
    });
  }

  function labelBoxesForEditor(page) {
    // Small numbered badges so a merchant can match a box in the "Manual per-box
    // position" fields to what they see — theme-editor preview only, mobile only.
    // Shopify.designMode alone isn't reliable enough here — it can be true on a
    // plain draft-theme preview tab too, not just inside the customizer's embedded
    // iframe. Require BOTH, so the badges never leak onto a normal preview visit.
    var inEditorFrame = window.self !== window.top;
    var show = window.Shopify && Shopify.designMode && inEditorFrame && window.innerWidth <= 700;
    var boxes = page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text');
    boxes.forEach(function (box, i) {
      var badge = box.querySelector(':scope > .damder-box-badge');
      if (!show) {
        if (badge) badge.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'damder-box-badge';
        box.appendChild(badge);
      }
      badge.textContent = i + 1;
    });
  }

  function intendedHeight(box) {
    var frame = box.closest('.damder-catalog-canvas__stage') || box.closest('.damder-catalog-canvas__page');
    var ph = frame ? frame.clientHeight : box.clientHeight;
    var m = (box.getAttribute('style') || '').match(/--h:\s*([\d.]+)%/);
    return m ? (ph * parseFloat(m[1])) / 100 : box.clientHeight;
  }

  function overflows(box) {
    // Width is the real constraint — size each label to fit its cell width, so it
    // stays as large as the wording allows (Hebrew ends up bigger than the longer
    // English). Height is kept lenient (1.5x the calibrated slot) so a normal single
    // line at a readable size doesn't trip it — only genuine multi-line wrapping /
    // overflowing paragraphs do.
    return box.scrollWidth > box.clientWidth + 0.5 ||
           box.scrollHeight > intendedHeight(box) * 1.5 + 2;
  }

  function fitPage(page) {
    var boxes = [].slice.call(page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text'));
    if (!boxes.length) return;
    // Mobile renders each half at twice the original artboard scale. Keep the
    // calibrated type size; measuring the visually-hidden source overlay would
    // otherwise force every line down to the emergency 50% minimum.
    var requestedScale = responsiveScale(page);
    // Every breakpoint keeps the same absolutely-positioned artboard, so one
    // code path fits them all: the phone just requests a larger scale via the
    // section's "Text scale — mobile" setting.
    if (getComputedStyle(boxes[0]).position !== 'absolute') return;

    // Group boxes into tiers by calibrated size, and reset every box to its base first.
    var tiers = {};
    boxes.forEach(function (box) {
      var base = primaryBase(box);
      if (!base) return;
      applyScale(box, requestedScale);
      var key = base.toFixed(1);
      (tiers[key] = tiers[key] || []).push(box);
    });

    // For each tier, step the whole group down until every box in it fits.
    Object.keys(tiers).forEach(function (key) {
      var group = tiers[key];
      var scale = requestedScale;
      var min = requestedScale * 0.5;
      for (var i = 0; i < 26; i++) {
        var bad = group.some(overflows);
        if (!bad) break;
        scale -= 0.03;
        if (scale < min) scale = min;
        group.forEach(function (b) { applyScale(b, scale); });
        if (scale === min) break;
      }
    });
  }

  function syncStage(page, isPhone) {
    // On a phone the 4:3 spread is only ~353px wide, which shrinks the calibrated
    // type to 4-8px. Widening the artboard inside a horizontally scrollable card
    // keeps every label in its printed table cell (cqi units scale with the stage)
    // while making it readable; the visitor swipes the card sideways to read across.
    var stage = page.querySelector(':scope > .damder-catalog-canvas__stage');
    if (isPhone) {
      if (stage) return;
      stage = document.createElement('div');
      stage.className = 'damder-catalog-canvas__stage';
      while (page.firstChild) stage.appendChild(page.firstChild);
      page.appendChild(stage);
    } else if (stage) {
      while (stage.firstChild) page.insertBefore(stage.firstChild, stage);
      stage.remove();
    }
  }

  function slotFrame(page) {
    return page.querySelector(':scope > .damder-catalog-canvas__stage') || page;
  }

  function centerInSlot(page) {
    // Each box is anchored by its TOP at the calibrated --y, so whenever the text
    // renders taller than its printed slot (longer translation, or simply a smaller
    // viewport where a line takes proportionally more height) it grows downwards and
    // drifts onto the rule beneath it — the client saw the grade names sitting on the
    // table lines. Centre the text on its slot instead, so it stays in its row.
    var frame = slotFrame(page);
    page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text').forEach(function (box) {
      var match = (box.getAttribute('style') || '').match(/--h:\s*([\d.]+)%/);
      var base = 0;
      if (match) {
        var slot = (frame.clientHeight * parseFloat(match[1])) / 100;
        var rendered = box.scrollHeight;
        if (rendered > slot + 1) base = -(rendered - slot) / 2;
      }
      box.dataset.damderBaseShift = base.toFixed(2);
      box.style.transform = base ? 'translateY(' + base.toFixed(2) + 'px)' : '';
    });
  }

  function resolveCollisions(page) {
    // A longer translation can still run a box into the one below it. Nudge only the
    // colliding box, on top of its centring offset, so nothing overlaps.
    var frame = slotFrame(page);
    var boxes = [].slice.call(page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text'));
    if (boxes.length < 2) return;
    boxes.sort(function (a, b) {
      return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
    });
    var gap = Math.max(2, frame.clientHeight * 0.004);
    boxes.forEach(function (box, index) {
      // A manual "Box tuning" nudge can itself be the thing that would create a new
      // collision (e.g. pulling a paragraph up into the label above it) — check
      // against where the box is ABOUT to sit, not where it currently sits, or the
      // manual shift could crash it into its neighbor undetected.
      var manualDy = viewportSuffix() === 'Mobile' ? (parseFloat(box.dataset.damderManualDy) || 0) : 0;
      var extra = 0;
      for (var i = 0; i < index; i++) {
        var prev = boxes[i].getBoundingClientRect();
        var cur = box.getBoundingClientRect();
        var curTop = cur.top + manualDy;
        var horizontal = Math.min(prev.right, cur.right) - Math.max(prev.left, cur.left);
        if (horizontal > 4 && prev.bottom + gap > curTop) {
          extra = Math.max(extra, prev.bottom + gap - curTop);
        }
      }
      var base = parseFloat(box.dataset.damderBaseShift || '0');
      var total = base + extra + manualDy;
      box.style.transform = total ? 'translateY(' + total.toFixed(2) + 'px)' : '';
    });
  }

  function growToFit(page) {
    // A page is a 4:3 card; on a phone that is only ~265px tall and the densest
    // Hebrew/English pages can't hold their text without cutting it off. Let ONLY
    // those pages grow taller until the text fits — pages that already fit (cover,
    // the grade chart) keep their exact 4:3 proportions, so nothing is distorted
    // where it matters. The background fills the taller card; the overlay text is
    // percent-positioned so it simply gains vertical breathing room.
    page.style.height = '';
    var width = page.clientWidth;
    if (!width) return;
    var baseHeight = width * 0.75;
    var pad = baseHeight * 0.05;
    var needed = baseHeight;
    page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text').forEach(function (box) {
      var m = (box.getAttribute('style') || '').match(/--y:\s*([\d.]+)%/);
      var y = m ? parseFloat(m[1]) / 100 : 0;
      var h = box.scrollHeight;
      if (y < 0.985) needed = Math.max(needed, (h + pad) / (1 - y));
    });
    // Cap the growth so a page can't become absurdly long; beyond this the text
    // would rather shrink (handled by fitPage) than push the card past ~1.9x.
    needed = Math.min(needed, baseHeight * 1.9);
    if (needed > baseHeight + 1) page.style.height = Math.ceil(needed) + 'px';
  }

  function buildMobileStory(page) {
    // "Mobile layout: Story" — on phones only, swap the fixed 4:3 brochure replica
    // (which forces the calibrated print type down to a 4-8px cqi size) for a plain
    // stacked card: full-width photo, then normal-flow heading/paragraph text sized
    // to match the readable grade cards further down this same page. Same wording,
    // same editor fields — just laid out for reading instead of printing. The chart
    // page (real table cells that must stay aligned) opts out via its own "Table /
    // chart" setting and is left completely untouched, as is every viewport above
    // mobile.
    var layout = page.dataset.mobileLayout || 'story';
    var isMobile = viewportSuffix() === 'Mobile';
    var existing = page.querySelector(':scope > .damder-catalog-canvas__mobile-story');

    if (!isMobile || layout !== 'story') {
      if (existing) existing.remove();
      page.classList.remove('damder-catalog-canvas__page--story');
      return;
    }

    page.classList.add('damder-catalog-canvas__page--story');
    if (existing) return;

    var story = document.createElement('div');
    story.className = 'damder-catalog-canvas__mobile-story';

    var bg = page.querySelector(':scope > .damder-catalog-canvas__background');
    if (bg) {
      var img = document.createElement('img');
      img.className = 'damder-catalog-canvas__mobile-story-img';
      img.src = bg.currentSrc || bg.src;
      if (bg.srcset) img.srcset = bg.srcset;
      img.sizes = '100vw';
      img.alt = bg.alt || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      story.appendChild(img);
    }

    var textWrap = document.createElement('div');
    textWrap.className = 'damder-catalog-canvas__mobile-story-text';

    // Adjacent boxes at the same calibrated tier are fragments of one flowing
    // sentence (hand-broken into brochure-width lines) — merge them back into a
    // single heading/paragraph instead of one choppy element per box.
    var boxes = [].slice.call(page.querySelectorAll('.damder-catalog-canvas__overlay > .damder-catalog-canvas__text'));
    var currentEl = null;
    var currentIsHeading = null;
    boxes.forEach(function (box) {
      // A single box often holds several text_XX fields joined by <br> tags (the
      // brochure's hand-broken print lines) — reading textContent directly drops
      // those breaks with no space and glues the words together. Turn each <br>
      // into a space first, same fix rescueOverflow() uses above.
      var tmp = document.createElement('div');
      tmp.innerHTML = box.innerHTML.replace(/<br\s*\/?>/gi, ' ');
      var text = tmp.textContent.replace(/\s+/g, ' ').trim();
      if (!text) return;
      var isHeading = primaryBase(box) >= 2.8;
      if (!currentEl || currentIsHeading !== isHeading) {
        currentEl = document.createElement(isHeading ? 'h2' : 'p');
        currentEl.className = isHeading
          ? 'damder-catalog-canvas__mobile-story-heading'
          : 'damder-catalog-canvas__mobile-story-body';
        currentEl.textContent = text;
        textWrap.appendChild(currentEl);
        currentIsHeading = isHeading;
      } else {
        currentEl.textContent += ' ' + text;
      }
    });

    story.appendChild(textWrap);
    page.appendChild(story);
  }

  function fitAll(root) {
    (root || document).querySelectorAll('.damder-catalog-canvas__page').forEach(function (page) {
      // Remove any slice nodes left by an earlier (wider) render before re-laying out.
      page.querySelectorAll(':scope > .damder-catalog-canvas__mobile-slice').forEach(function (slice) {
        slice.remove();
      });
      markJustifyBoxes(page);
      syncStage(page, false);
      applyEditorControls(page);
      applyBoxTuning(page);
      page.style.height = '';
      fitPage(page);
      applyManualBoxScale(page);
      rescueOverflow(page);
      if (viewportSuffix() === 'Mobile') centerInSlot(page);
      // Only nudge boxes that actually overlap (long translations). The original
      // positions are calibrated to the Hebrew wording, so Hebrew is left untouched;
      // English gets just the minimum nudge to stop headings sitting on body copy.
      resolveCollisions(page);
      buildMobileStory(page);
      labelBoxesForEditor(page);
    });
  }

  function boot() {
    fitAll();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { fitAll(); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () { fitAll(); }, 160);
  });

  document.addEventListener('shopify:section:load', function (e) { fitAll(e.target); });
})();
